// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import ProductListProductCard from "@insite/content-library/Widgets/ProductList/ProductListProductCard";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPanel } from "@insite/mobius/AccordionSection";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@pacific/App/Components/Notify";
import loadRealtimeData from "@pacific/App/Handlers/ProductList/LoadRealtimeData";
import { CategoryTree, CustomAppState } from "@pacific/App/Store/Reducers";
import { SectionTitle } from "@pacific/App/Widgets/DesignSystem/DesignSystem.helpers";
import ManagedAccordionSection from "@pacific/MobiusOverrides/AccordionSection/ManagedAccordionSection";
import { color, pluralize, xScreen } from "@pacific/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImage = "showImage",
    showBrand = "showBrand",
    showTitle = "showTitle",
    showPartNumbers = "showPartNumbers",
    showAvailability = "showAvailability",
    showAttributes = "showAttributes",
    showPrice = "showPrice",
    showAddToList = "showAddToList",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAttributes]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const {
        pages: {
            productList: { isLoading, parameter },
        },
    } = state;
    const categoryTreeKey = getDataViewKey(parameter);
    return {
        isLoading,
        productsDataView: getProductListDataView(state),
        // XNG-Changes
        products: state.data.products.byId,
        categoryTree: state.xData[categoryTreeKey]?.categoryTree || [],
    };
};

const mapDispatchToProps = {
    loadRealtimeData,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListCardListStyles {
    wrapper?: InjectableCss;
    cardList?: CardListStyles;
    noProductsText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    accordion?: AccordionPresentationProps;
    accordionTitle?: TypographyPresentationProps;
    cardContainerStyles?: CardContainerStyles;
}

export const listStyles: ProductListCardListStyles = {
    wrapper: {
        css: css`
            margin-top: 20px;
            border: none;
            ${xScreen("lte", "md")} {
                margin-top: 32px;
            }
        `,
    },
    cardList: {
        gridContainer: {
            gap: 22,
        },
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    accordion: {
        css: css`
            margin-bottom: 8px;
            & ${AccordionSectionPanel} {
                background: ${color("common.ui1")};
            }
        `,
    },
    accordionTitle: {
        variant: "h6",
        css: css`
            margin: 0;
            width: 100%;

            > span {
                float: right;
                margin: 0 15px 0 0;
            }
        `,
    },
    cardContainerStyles: {
        cardDivider: {
            css: css`
                width: 100%;
            `,
        },
        gridItem: {
            css: css`
                border: none;
            `,
        },
    },
};

const styles = listStyles;

const ProductListCardList = ({
    isLoading,
    productsDataView,
    fields,
    products,
    categoryTree,
    loadRealtimeData,
}: Props) => {
    const [activeGroup, setActiveGroup] = useState<string | undefined>();

    if (isLoading && !productsDataView.value && !categoryTree?.length) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
            </StyledWrapper>
        );
    }

    if (!isLoading && (!productsDataView.value || !categoryTree?.length)) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.centeringWrapper} data-test-selector="productListNoneFound">
                    {/* XNG-Change */}
                    <Notify type="warning" {...styles.noProductsText}>
                        {siteMessage("Custom_RecentlyPurchased_NoProductsFound")}
                    </Notify>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const handleToggle = (id: string, productIds: string[]) => {
        if (id === activeGroup) {
            setActiveGroup(undefined);
        } else {
            setActiveGroup(id);
            loadRealtimeData({ productIds } as any);
        }
    };

    const renderTitle = (level: CategoryTree, title?: string): any => {
        if (!title) {
            title = level.shortDescription;
        }
        if (level.subCategories.length === 0) {
            return (
                <Typography {...styles.accordionTitle}>
                    {title}{" "}
                    <Typography variant="h6" as="span">
                        {`${level.productIds.length} ${translate(pluralize(level.productIds.length, "Item"))}`}
                    </Typography>
                </Typography>
            );
        }
        return renderTitle(level.subCategories[0], title + level.subCategories[0].shortDescription);
    };

    const buildCategoryTree = (categoryTree: CategoryTree[]): any => {
        return categoryTree.map((level: CategoryTree) => {
            if (level.subCategories.length > 0) {
                return (
                    <StyledWrapper key={level.id}>
                        <SectionTitle>{level.shortDescription}</SectionTitle>
                        {buildCategoryTree(level.subCategories)}
                    </StyledWrapper>
                );
            }
            return (
                <Accordion {...styles.accordion} key={level.id} headingLevel={4}>
                    <ManagedAccordionSection
                        title={renderTitle(level)}
                        initialExpanded={false}
                        expanded={`${level.id}` === activeGroup}
                        onTogglePanel={() => {
                            handleToggle(level.id!, level.productIds);
                        }}
                    >
                        {level.productIds.map(id => {
                            const product = products[id];
                            if (product) {
                                return (
                                    <CardContainer extendedStyles={styles.cardContainerStyles} key={product.id}>
                                        <ProductListProductContext product={product}>
                                            <ProductListProductCard
                                                showAvailability={fields.showAvailability}
                                                showBrand={fields.showBrand}
                                                showPartNumbers={fields.showPartNumbers}
                                                showTitle={fields.showTitle}
                                                showAddToList={fields.showAddToList}
                                                showAttributes={fields.showAttributes}
                                                showCompare={false}
                                                showImage={fields.showImage}
                                                showPrice={fields.showPrice}
                                                key={product.id}
                                            />
                                        </ProductListProductContext>
                                    </CardContainer>
                                );
                            }
                            return null;
                        })}
                    </ManagedAccordionSection>
                </Accordion>
            );
        });
    };

    return (
        <StyledWrapper {...styles.wrapper} id="productListCardList">
            <ProductListPageDataContext.Consumer>
                {({ ref }) => {
                    return ref ? <span ref={ref} tabIndex={-1} /> : undefined;
                }}
            </ProductListPageDataContext.Consumer>
            <LoadingOverlay loading={isLoading}>{buildCategoryTree(categoryTree)}</LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListCardList),
    definition: {
        group: "My Account",
        displayName: "Card List",
        allowedContexts: ["RecentlyPurchasedProductsPage"] as any,
        fieldDefinitions: [
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Show Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showAvailability,
                displayName: "Show Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPrice,
                displayName: "Show Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showAddToList,
                displayName: "Show Add To List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showImageBadges,
                displayName: "Show Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.showTextBadges,
                displayName: "Show Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 13,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 14,
            },
        ],
    },
};

export default widgetModule;
